import { UPLOAD_MESSAGES, UPLOAD_CONFIG } from "../components/Common/constants";
import { LOGGER_MESSAGES } from "../components/Common/LoggerConstants";

export const base64ToBlob = (dataUrl) => {
  const [header, base64Data] = dataUrl.split(",");
  const mimeMatch = header.match(/:(.*?);/);
  const mime = (mimeMatch && mimeMatch[1]) ? mimeMatch[1] : "application/octet-stream";
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
};

const validatePdfJson = async (fileObj) => {
  const arrayBuffer = await fileObj.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);

  const resp = await fetch(UPLOAD_CONFIG.PDF_VALIDATION_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ base64 }),
  });

  if (!resp.ok) {
    let json = null;
    try { json = await resp.json(); } catch (e) { console.error(LOGGER_MESSAGES.PDF_PARSE_ERROR_RESPONSE, e); }
    const message = json?.message ?? UPLOAD_MESSAGES.INVALID_PDF;
    return { blocked: true, message };
  }

  const json = await resp.json();
  if (json?.blocked) {
    const message = json.message ?? UPLOAD_MESSAGES.INVALID_PDF;
    return { blocked: true, message };
  }

  return { blocked: false };
};

const isAllowedType = (fileObj) => {
  return !!fileObj?.type?.match(UPLOAD_CONFIG.ALLOWED_TYPE_REGEX);
};

const isPdfFile = (fileObj) => {
  return !!fileObj?.type?.toLowerCase().includes("pdf");
};

const readFileAsDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error(UPLOAD_MESSAGES.FILE_READER_ERROR));
    reader.readAsDataURL(file);
  });
};

export async function validateUploadFile(fileObj) {
  if (!fileObj) return { valid: false, errorType: 'invalid', message: UPLOAD_MESSAGES.NO_FILE };

  if (fileObj.size >= UPLOAD_CONFIG.MAX_FILE_SIZE) {
    return { valid: false, errorType: 'size', message: UPLOAD_MESSAGES.INVALID_SIZE };
  }

  if (!isAllowedType(fileObj)) {
    return { valid: false, errorType: 'type', message: UPLOAD_MESSAGES.INVALID_TYPE };
  }

  if (isPdfFile(fileObj)) {
    try {
      const resp = await validatePdfJson(fileObj);
      if (resp?.blocked) {
        return { valid: false, errorType: 'pdf', message: resp.message ?? UPLOAD_MESSAGES.INVALID_PDF };
      }
    } catch (err) {
      console.error("Failed to validate PDF upload:", err);
      return { valid: false, errorType: 'pdf', message: UPLOAD_MESSAGES.INVALID_PDF };
    }
  }

  let dataUrl;
  try {
    dataUrl = await readFileAsDataUrl(fileObj);
  } catch (err) {
    console.error(UPLOAD_MESSAGES.FILE_READER_ERROR, err);
    return { valid: false, errorType: 'pdf', message: UPLOAD_MESSAGES.FILE_READER_ERROR };
  }

  return {
    valid: true,
    doc: {
      documentName: fileObj.name,
      base64Content: dataUrl,
      mimeType: fileObj.type,
    },
  };
}

export function validationStateFromResult(res) {
  const defaultPdfMessage = UPLOAD_MESSAGES.INVALID_PDF;

  if (!res || res.valid) {
    return { invalidSize: false, invalidType: false, invalidPdf: false, invalidPdfMessage: '' };
  }

  const errorStateMap = {
    size: { invalidSize: true, invalidType: false, invalidPdf: false },
    type: { invalidSize: false, invalidType: true, invalidPdf: false },
    pdf: { invalidSize: false, invalidType: false, invalidPdf: true },
  };

  const resolvedState = errorStateMap[res.errorType] || errorStateMap.pdf;

  return {
    ...resolvedState,
    invalidPdfMessage: res.message || (resolvedState.invalidPdf ? defaultPdfMessage : ''),
  };
}
